/**
 * Route parsers — GPX and Google Maps URL → uniform shape.
 *
 * Output shape (kept minimal so the rest of the tools UI doesn't
 * need to know which format the route came from):
 *
 *   {
 *     name: string | null,        // <name> from GPX, or null
 *     points: Array<{ lat, lng, ele?, time? }>,
 *     waypoints: Array<{ lat, lng, name? }>,   // named stops (POIs / Maps URL stops)
 *     source: "gpx" | "google_maps_url" | "go_route",
 *   }
 *
 * Throws `RouteParseError` with a user-facing PT-PT message on
 * malformed input. Keep the messages short — they go straight to a
 * `toast.error()`.
 *
 * (KML support removed 2026-05-22 — formato deprecado.)
 */
import { gpx as gpxToGeoJSON } from "@tmcw/togeojson";

// b99.10.4 (2026-05-23) — Fuel-brand detection in waypoint names.
// Mirrors `services/overpass.py::classify_fuel_from_name` (backend
// runs this for Google Maps URL imports; this module runs it for
// GPX imports + any other client-side path). Keep both lists in
// sync. Order matters: longer/more specific brands first.
const FUEL_BRAND_TOKENS = [
  ["repsol", "Repsol"],
  ["galp", "Galp"],
  ["cepsa", "Cepsa"],
  ["petroibérica", "Petroibérica"],
  ["petroiberica", "Petroibérica"],
  ["intermarché", "Intermarché"],
  ["intermarche", "Intermarché"],
  ["carrefour", "Carrefour"],
  ["leclerc", "Leclerc"],
  ["auchan", "Auchan"],
  ["prio", "Prio"],
  ["shell", "Shell"],
  ["moeve", "Moeve"],
  ["avia", "Avia"],
  ["totalenergies", "TotalEnergies"],
  ["total", "Total"],
  ["eni", "Eni"],
  ["agip", "Agip"],
  ["esso", "Esso"],
  ["q8", "Q8"],
  ["gasum", "Gasum"],
  ["bp", "BP"],
];
const FUEL_GENERIC_TOKENS = [
  "posto de combust",
  "estação de servi",
  "estacion de servi",
  "estación de servi",
  "gasolinera",
  "tankstelle",
  "station-service",
  "station service",
  "stazione di servi",
  "fuel station",
  "gas station",
];
// b99.10.4 — Same word-boundary policy as the backend. The negative
// lookarounds reject substring matches like "av**eni**da" → "Eni".
// Accented characters listed explicitly because `\b` is unreliable
// across locales.
const FUEL_WORD_CHARS = "a-z0-9àáâãäçèéêëìíîïñòóôõöùúûü";
function escapeRegExp(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}
export function classifyFuelFromName(name) {
  if (!name) return { isFuel: false, brand: null };
  const lower = String(name).toLowerCase();
  for (const [token, canonical] of FUEL_BRAND_TOKENS) {
    const re = new RegExp(
      `(?<![${FUEL_WORD_CHARS}])${escapeRegExp(token)}(?![${FUEL_WORD_CHARS}])`,
    );
    if (re.test(lower)) return { isFuel: true, brand: canonical };
  }
  for (const token of FUEL_GENERIC_TOKENS) {
    if (lower.includes(token)) return { isFuel: true, brand: null };
  }
  return { isFuel: false, brand: null };
}

export class RouteParseError extends Error {
  constructor(message) {
    super(message);
    this.name = "RouteParseError";
  }
}

// ─── GPX (via @tmcw/togeojson) ───────────────────────────────────

function parseXmlString(xmlString) {
  if (typeof DOMParser === "undefined") {
    throw new RouteParseError("XML parser indisponível neste browser.");
  }
  const doc = new DOMParser().parseFromString(xmlString, "application/xml");
  // DOMParser doesn't throw on malformed XML; it inserts a
  // `<parsererror>` node. Detect it explicitly.
  if (doc.getElementsByTagName("parsererror").length > 0) {
    throw new RouteParseError("Ficheiro inválido (XML mal formado).");
  }
  return doc;
}

/**
 * Flatten any GeoJSON FeatureCollection into a single chronological
 * point list. We accept LineString, MultiLineString and Point features
 * because real-world GPX files mix them (a track + standalone POIs).
 *
 * Points come back as `[lng, lat, ele?]` per GeoJSON convention; we
 * flip to `{lat, lng}` for ergonomics elsewhere in the app.
 *
 * Named Point features (typical `<wpt>` GPX tags) are ALSO collected
 * into a separate `waypoints` array so the map can label them. The
 * same Point still goes into `points` to preserve legacy behaviour
 * for distance/exports.
 */
function flattenGeoJSON(geojson) {
  const points = [];
  const waypoints = [];
  let name = null;
  // First pass: detect if this GeoJSON has any actual TRACK geometry
  // (LineString / MultiLineString). When it does, standalone Point
  // features are pure labels (GPX <wpt>) and MUST NOT be
  // interleaved into the polyline — doing so creates large straight
  // "haversine" jumps between distant labels (bug 2026-05-14: re-
  // importing an exported GPX showed the 2 Maps-URL waypoints as
  // teleport segments).
  let hasTrackGeometry = false;
  for (const feat of geojson.features || []) {
    const t = feat.geometry?.type;
    if (t === "LineString" || t === "MultiLineString") {
      hasTrackGeometry = true;
      break;
    }
  }
  for (const feat of geojson.features || []) {
    if (!name && feat.properties?.name) name = feat.properties.name;
    const g = feat.geometry;
    if (!g) continue;
    if (g.type === "Point") {
      const [lng, lat, ele] = g.coordinates;
      const wpName = feat.properties?.name;
      // Always record named Points as waypoints so the map can label
      // them. Only fall back to using them as track points when the
      // file has no track geometry at all (pure-wpt GPX exports).
      if (wpName) waypoints.push({ lat, lng, name: wpName });
      if (!hasTrackGeometry) points.push({ lat, lng, ele });
    } else if (g.type === "LineString") {
      for (const [lng, lat, ele] of g.coordinates) points.push({ lat, lng, ele });
    } else if (g.type === "MultiLineString") {
      for (const line of g.coordinates) {
        for (const [lng, lat, ele] of line) points.push({ lat, lng, ele });
      }
    }
    // Optional: pull <time> from coordTimes (togeojson convention).
    const coordTimes = feat.properties?.coordTimes;
    if (Array.isArray(coordTimes) && coordTimes.length === points.length) {
      for (let i = 0; i < coordTimes.length; i++) {
        if (coordTimes[i]) points[i].time = coordTimes[i];
      }
    }
  }
  return { name, points, waypoints };
}

export function parseGPX(xmlString) {
  const doc = parseXmlString(xmlString);
  const geojson = gpxToGeoJSON(doc);
  // b99.10.43 (2026-05-21) — Extract per-track structure so that a
  // GPX exported by the Editor (one `<trk>` per etapa) round-trips
  // back into N etapas. Each LineString / MultiLineString feature
  // becomes one track with its own `name`. We still produce the
  // legacy flat `points` field (concat of all tracks) and a global
  // `waypoints` list for backward compat with callers that don't
  // care about stage structure.
  const tracks = [];
  const allWaypoints = [];
  let docName = null;
  // First pass: detect if there's any track geometry (mirrors the
  // logic in `flattenGeoJSON` — without it, standalone <wpt> nodes
  // would be misinterpreted as polyline points).
  let hasTrackGeometry = false;
  for (const feat of geojson.features || []) {
    // EDITOR-POI-AS-VIA — ignora as features `<rte>` (togeojson marca-as
    // com `_gpxType:"rte"`). O MotoTrips exporta `<rte>` só para apps de
    // navegação passarem pelos PDIs; o nosso round-trip usa `<trk>` (track)
    // + `<wpt motri-poi>` (marcadores). Sem este filtro, o `<rte>` voltaria
    // como uma 2.ª LineString → etapa duplicada.
    if (feat.properties?._gpxType === "rte") continue;
    const t = feat.geometry?.type;
    if (t === "LineString" || t === "MultiLineString") {
      hasTrackGeometry = true;
      break;
    }
  }
  for (const feat of geojson.features || []) {
    if (feat.properties?._gpxType === "rte") continue;
    if (!docName && feat.properties?.name) docName = feat.properties.name;
    const g = feat.geometry;
    if (!g) continue;
    if (g.type === "Point") {
      const [lng, lat, ele] = g.coordinates;
      const wpName = feat.properties?.name;
      if (wpName) {
        // b99.10.4 — Brand classification on GPX waypoints (mirrors
        // backend's logic for GMaps URL imports). Lets the editor
        // mark Repsol / BP / Galp etc. with the fuel-pump icon.
        const { isFuel, brand } = classifyFuelFromName(wpName);
        const wp = { lat, lng, name: wpName };
        if (isFuel) {
          wp.poi_kind = "fuel";
          if (brand) wp.poi_brand = brand;
        }
        allWaypoints.push(wp);
      }
      // Pure-wpt GPX (no <trk>): turn the wpts into a single track.
      if (!hasTrackGeometry) {
        if (tracks.length === 0) tracks.push({ name: docName, points: [] });
        tracks[0].points.push({ lat, lng, ele });
      }
    } else if (g.type === "LineString") {
      const points = g.coordinates.map(([lng, lat, ele]) => ({ lat, lng, ele }));
      const coordTimes = feat.properties?.coordTimes;
      if (Array.isArray(coordTimes) && coordTimes.length === points.length) {
        for (let i = 0; i < coordTimes.length; i++) {
          if (coordTimes[i]) points[i].time = coordTimes[i];
        }
      }
      tracks.push({ name: feat.properties?.name || null, points });
    } else if (g.type === "MultiLineString") {
      // MultiLineString = one <trk> with multiple <trkseg>. Per spec
      // each <trkseg> is a discontinuity, NOT a separate etapa — so
      // we concatenate into a single track. (Editor exports always
      // emit one <trkseg> per <trk>, so this branch only triggers
      // on third-party files.)
      const points = [];
      for (const line of g.coordinates) {
        for (const [lng, lat, ele] of line) points.push({ lat, lng, ele });
      }
      tracks.push({ name: feat.properties?.name || null, points });
    }
  }
  // Fase C (2026-06-19) — GPX standard de TERCEIROS baseado em `<rte>` (ex.:
  // rotas Garmin/BaseCamp, sem `<trk>`). togeojson marca o `<rte>` como
  // LineString `_gpxType:"rte"` (ignorada acima). Aqui lemos os `<rtept>`
  // crus: a sequência vira geometria e os `<rtept>` NOMEADOS viram
  // PARAGENS (stops). Ficheiros do MotoTrips (creator="MotoTrips") são
  // ignorados — esses usam `<trk>`+`<wpt>` e o `<rte>` é só para navegação.
  const _gpxCreator = doc.documentElement?.getAttribute?.("creator") || "";
  const isMotoTripsGpx = _gpxCreator.includes("MotoTrips");
  if (!isMotoTripsGpx && tracks.every((t) => !t.points.length)) {
    const rawRtes = Array.from(doc.getElementsByTagName("rte"));
    for (const node of rawRtes) {
      let rteName = null;
      const rtepts = [];
      for (const child of Array.from(node.childNodes)) {
        if (child.nodeType !== 1) continue;
        const tag = child.localName || child.tagName;
        if (tag === "name" && !rteName) rteName = child.textContent?.trim() || null;
        else if (tag === "rtept" && typeof child.getAttribute === "function") {
          const lat = parseFloat(child.getAttribute("lat"));
          const lng = parseFloat(child.getAttribute("lon"));
          if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
          let nm = null;
          for (const c2 of Array.from(child.childNodes)) {
            if (c2.nodeType === 1 && (c2.localName || c2.tagName) === "name") {
              nm = c2.textContent?.trim() || null; break;
            }
          }
          rtepts.push({ lat, lng, name: nm });
        }
      }
      if (rtepts.length < 2) continue;
      tracks.push({ name: rteName, points: rtepts.map((p) => ({ lat: p.lat, lng: p.lng })) });
      // Os rtepts NOMEADOS entram em `allWaypoints` → a atribuição normal
      // marca-os como paragens (stop) via `pointsFromLegacy`.
      for (const p of rtepts) {
        if (!p.name) continue;
        const { isFuel, brand } = classifyFuelFromName(p.name);
        const wp = { lat: p.lat, lng: p.lng, name: p.name };
        if (isFuel) { wp.poi_kind = "fuel"; if (brand) wp.poi_brand = brand; }
        allWaypoints.push(wp);
      }
    }
  }

  const flatPoints = tracks.flatMap((t) => t.points);
  if (!flatPoints.length) throw new RouteParseError("GPX sem pontos válidos.");
  // b99.10.56 (2026-05-21) — Per-etapa wpt attribution via
  // `<type>motri-etapa:NAME</type>` marker (emitted by `toGPX`). We
  // parse raw `<wpt>` DOM nodes directly so we can read the `<type>`
  // child reliably across DOMParser implementations. When the marker
  // is present we override the spatial-attribution heuristic with
  // the explicit etapa name — fixes the circular-trip bug where
  // start/end joins of the FIRST and LAST etapa (same coords) were
  // duplicated into both.
  const ETAPA_PREFIX = "motri-etapa:";
  // EDITOR-ADD-POI (2026-06-10) — paragens manuais são exportadas como
  // `<wpt><type>motri-poi:CATEGORY</type></wpt>`. No re-import têm de
  // voltar como `pois` (marcadores) e NUNCA como stops de routing —
  // senão a primeira/última paragem era promovida a Início/Fim e a
  // geometria colapsava nos pontos das paragens.
  const POI_PREFIX = "motri-poi:";
  const rawWpts = Array.from(doc.getElementsByTagName("wpt"));
  const annotatedWpts = rawWpts.map((node) => {
    const lat = parseFloat(node.getAttribute("lat"));
    const lng = parseFloat(node.getAttribute("lon"));
    let name = null;
    let etapa = null;
    let poiCategory = null;
    for (const child of Array.from(node.childNodes)) {
      if (child.nodeType !== 1) continue; // ELEMENT_NODE
      const tag = child.localName || child.tagName;
      if (tag === "name" && !name) name = child.textContent?.trim() || null;
      else if (tag === "type") {
        const t = child.textContent?.trim() || "";
        if (t.startsWith(ETAPA_PREFIX)) etapa = t.slice(ETAPA_PREFIX.length).trim();
        else if (t.startsWith(POI_PREFIX)) poiCategory = t.slice(POI_PREFIX.length).trim() || "other";
      }
    }
    return { lat, lng, name, etapa, poiCategory };
  });

  // ─── Separar POIs (motri-poi) dos waypoints de routing ───────────
  const poiList = [];
  for (const aw of annotatedWpts) {
    if (aw.poiCategory != null && aw.name && Number.isFinite(aw.lat) && Number.isFinite(aw.lng)) {
      poiList.push({
        id: `poi_gpx_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`,
        lat: aw.lat,
        lng: aw.lng,
        name: aw.name,
        category: aw.poiCategory,
        source: "manual",
      });
    }
  }
  // As paragens também entraram em `allWaypoints` (via features Point);
  // remove-as para não serem tratadas como stops.
  if (poiList.length) {
    const poiKey = (la, ln) => `${la.toFixed(5)},${ln.toFixed(5)}`;
    const poiSet = new Set(poiList.map((p) => poiKey(p.lat, p.lng)));
    for (let i = allWaypoints.length - 1; i >= 0; i--) {
      const w = allWaypoints[i];
      if (Number.isFinite(w?.lat) && poiSet.has(poiKey(w.lat, w.lng))) allWaypoints.splice(i, 1);
    }
  }

  const hasEtapaMarkers = annotatedWpts.some((w) => w.etapa);
  for (const t of tracks) { t.waypoints = []; t.pois = []; }

  // Atribui as paragens às tracks (1 track → todas; N tracks → mais próxima).
  if (poiList.length) {
    if (tracks.length === 1) {
      tracks[0].pois = poiList;
    } else if (tracks.length > 1) {
      const dSq = (a, b) => { const dl = a.lat - b.lat; const dn = a.lng - b.lng; return dl * dl + dn * dn; };
      for (const poi of poiList) {
        let best = null;
        let bestD = Infinity;
        for (const t of tracks) {
          if (!t.points.length) continue;
          const step = Math.max(1, Math.floor(t.points.length / 200));
          for (let i = 0; i < t.points.length; i += step) {
            const d = dSq(poi, t.points[i]);
            if (d < bestD) { bestD = d; best = t; }
          }
        }
        (best || tracks[0]).pois.push(poi);
      }
    }
  }

  if (hasEtapaMarkers && tracks.length > 0) {
    // Build a name → track map (preserving first match if duplicates).
    const byName = new Map();
    for (const t of tracks) {
      if (t.name && !byName.has(t.name)) byName.set(t.name, t);
    }
    for (const aw of annotatedWpts) {
      if (!aw.name || !Number.isFinite(aw.lat) || !Number.isFinite(aw.lng)) continue;
      const target = aw.etapa ? byName.get(aw.etapa) : null;
      if (target) {
        target.waypoints.push({ lat: aw.lat, lng: aw.lng, name: aw.name });
      }
      // Unmarked wpts when markers are present: ignored (likely
      // third-party additions); spatial fallback would re-introduce
      // the bug we're fixing.
    }
  } else if (tracks.length > 1 && allWaypoints.length > 0) {
    // b99.10.50 (2026-05-21) — GPX spec stores `<wpt>` at document
    // level (not nested under `<trk>`), so for third-party files
    // (no `motri-etapa` marker) we fall back to spatial attribution:
    // for each `<wpt>`, find the track whose closest `<trkpt>` is
    // nearest, and assign the wpt to that track's `waypoints` list.
    const distSq = (a, b) => {
      const dlat = a.lat - b.lat;
      const dlng = a.lng - b.lng;
      return dlat * dlat + dlng * dlng;
    };
    // ~50m threshold (squared) for "junction" attribution: a wpt that
    // sits exactly on a track's start OR end is also bound to that
    // adjacent track, even if the closest centroid points to a
    // different one.
    const JUNCTION_SQ = 0.0005 * 0.0005;
    for (const wpt of allWaypoints) {
      let bestDist = Infinity;
      let bestTrack = null;
      const adjacent = new Set();
      for (const t of tracks) {
        if (!t.points.length) continue;
        const startD = distSq(wpt, t.points[0]);
        const endD = distSq(wpt, t.points[t.points.length - 1]);
        if (startD < JUNCTION_SQ || endD < JUNCTION_SQ) adjacent.add(t);
        // Sample every Nth point for speed (tracks can have 6k+ pts).
        const step = Math.max(1, Math.floor(t.points.length / 200));
        for (let i = 0; i < t.points.length; i += step) {
          const d = distSq(wpt, t.points[i]);
          if (d < bestDist) { bestDist = d; bestTrack = t; }
        }
      }
      if (adjacent.size > 0) {
        for (const t of adjacent) t.waypoints.push(wpt);
      } else if (bestTrack) {
        bestTrack.waypoints.push(wpt);
      }
    }
  } else if (tracks.length === 1) {
    tracks[0].waypoints = allWaypoints;
  }
  // EDITOR-ROUTE-OPTIONS-GPX (2026-06-17) — relê as opções de rota
  // (`<trk><extensions><motriRouteOptions avoidHighways avoidTolls/>`)
  // emitidas pelo `toGPX` e anexa-as à track (tri-estado: "true"=evita,
  // "false"=permite/conhecido, ausente=desconhecido). Match por nome com
  // fallback por ordem (o Editor emite 1 `<trk>` por etapa, em ordem).
  const rawTrks = Array.from(doc.getElementsByTagName("trk"));
  const trkOptions = rawTrks.map((node) => {
    let trkName = null;
    let routeOptions = null;
    for (const child of Array.from(node.childNodes)) {
      if (child.nodeType !== 1) continue;
      const tag = child.localName || child.tagName;
      if (tag === "name" && !trkName) trkName = child.textContent?.trim() || null;
      else if (tag === "extensions") {
        for (const ext of Array.from(child.childNodes)) {
          if (ext.nodeType !== 1) continue;
          const etag = ext.localName || ext.tagName;
          if (etag === "motriRouteOptions" && typeof ext.getAttribute === "function") {
            const ah = ext.getAttribute("avoidHighways");
            const at = ext.getAttribute("avoidTolls");
            routeOptions = {
              avoidHighways: ah === "true" ? true : (ah === "false" ? false : null),
              avoidTolls: at === "true" ? true : (at === "false" ? false : null),
            };
          }
        }
      }
    }
    return { name: trkName, routeOptions };
  });
  for (let i = 0; i < tracks.length; i++) {
    const byName = trkOptions.find((o) => o.routeOptions && o.name && o.name === tracks[i].name);
    const byIdx = trkOptions[i] && trkOptions[i].routeOptions ? trkOptions[i] : null;
    const opt = byName || byIdx;
    if (opt && opt.routeOptions) tracks[i].routeOptions = opt.routeOptions;
  }
  return {
    name: docName,
    points: flatPoints,
    tracks,
    waypoints: allWaypoints,
    pois: poiList,
    source: "gpx",
  };
}

// ─── Google Maps URL ──────────────────────────────────────────────

/**
 * Quick client-side acceptance check for a Google Maps URL.
 *
 * Used to gate the "Importar" button before we hit the backend. It
 * returns `true` for any of the three shapes the backend can handle:
 *   - `https://maps.app.goo.gl/<short>`  (short link)
 *   - `https://goo.gl/maps/<short>`      (legacy short link)
 *   - `https://www.google.com/maps/...`  (full URL)
 *
 * The actual extraction is done server-side by
 * `POST /api/utils/parse-gmaps-url` (authed) or
 * `POST /api/public/utils/parse-gmaps-url` (anonymous) because:
 *   1. Short links (`maps.app.goo.gl`) require a redirect resolution
 *      which is CORS-blocked in the browser.
 *   2. Place-name segments (`/dir/Porto/Lisboa`) need the coords from
 *      the `data=` block including correct handling of `!1m5!1m1!1s{pid}`
 *      explicit-stop blocks vs `!3m4!1m2` snap-to-road hints (see bug
 *      2026-05-15 — PDIs nos sítios errados). Centralising this on
 *      the backend means a single fix propagates to all consumers.
 *
 * b99.9.25 — the legacy client-side `parseGoogleMapsURL` fallback has
 * been removed. It only handled the trivial `/dir/lat,lng/lat,lng`
 * shape and lacked the explicit-stop vs snap-to-road logic, so it
 * was a divergence vector waiting to happen. If you need an offline
 * pre-check, use `isGoogleMapsURL(url)` (synchronous, no network).
 */
export function isGoogleMapsURL(url) {
  if (typeof url !== "string") return false;
  const t = url.trim();
  if (!t) return false;
  return /maps\.app\.goo\.gl|goo\.gl\/maps|google\.[a-z.]+\/maps/i.test(t);
}

/**
 * Backend-backed parser for Google Maps URLs. Resolves short links
 * (`maps.app.goo.gl`) and place-name waypoints. The backend is the
 * SINGLE source of truth for parsing — including the explicit-stop
 * vs snap-to-road disambiguation (b99.9.25). There is no longer a
 * client-side fallback.
 *
 * Throws `RouteParseError` on backend failure with a friendly PT-PT
 * message.
 *
 * @param {string} url
 * @param {{ post: (path: string, body: object) => Promise<{data: object}> }} apiClient
 *   The shared `@/lib/api` axios client. Passed in so this module
 *   stays framework-agnostic and unit-testable without the network.
 */
export async function parseGoogleMapsURLViaBackend(url, apiClient) {
  if (typeof url !== "string" || !url.trim()) {
    throw new RouteParseError("URL vazio.");
  }
  if (!isGoogleMapsURL(url)) {
    throw new RouteParseError("Não parece um URL do Google Maps.");
  }
  // b99.10.99 (2026-05-25) — Module-level dedup. The Public Trip
  // viewer mounts one `<SegmentRouteCard>` per segment, each
  // mounting a `<RouteMapViewer>` that calls this function on
  // mount. A trip with 10 segments → 10 parallel requests against
  // `/api/public/utils/parse-gmaps-url` (after the authed call
  // 401's for anonymous visitors). Coupled with a few concurrent
  // visitors on the same NAT/ISP, the 120/min IP limit was being
  // burned in seconds (PUB-RATE-LIMIT-429). The TripOverviewMap
  // already had its own dedup (`fetchGmapsWaypoints._cache`) but
  // RouteMapViewer didn't, and the editor path doesn't either.
  // Centralising dedup HERE covers all three consumers.
  //   - concurrent identical requests → single in-flight promise
  //   - resolved URLs → permanent in-memory cache for the page
  //     lifetime (parser is pure: URL → waypoints)
  //   - errors are NOT cached so transient 5xx are retryable
  const cacheKey = url.trim();
  const cache = parseGoogleMapsURLViaBackend._cache
    || (parseGoogleMapsURLViaBackend._cache = new Map());
  const inflight = parseGoogleMapsURLViaBackend._inflight
    || (parseGoogleMapsURLViaBackend._inflight = new Map());
  if (cache.has(cacheKey)) return cache.get(cacheKey);
  if (inflight.has(cacheKey)) return inflight.get(cacheKey);
  const promise = _parseGoogleMapsURLViaBackendImpl(cacheKey, apiClient);
  inflight.set(cacheKey, promise);
  try {
    const result = await promise;
    cache.set(cacheKey, result);
    return result;
  } finally {
    inflight.delete(cacheKey);
  }
}

async function _parseGoogleMapsURLViaBackendImpl(url, apiClient) {
  let res;
  try {
    res = await apiClient.post("/api/utils/parse-gmaps-url", { url });
  } catch (e) {
    // b99.9.50 (2026-05-15) — fallback to the public endpoint when the
    // user is anonymous. The authed `/api/utils/parse-gmaps-url` rejects
    // un-logged visitors with 401, surfacing as "Not authenticated"
    // inside `RouteMapViewer` on the public trip sheet. The public
    // `/api/public/utils/parse-gmaps-url` has the SAME parser body
    // behind an IP rate-limit (30 req/min) — exactly what the viewer
    // needs. Production trip_8dfe3aff0bfc, anonymous → confirmed 401.
    const status = e?.response?.status;
    if (status === 401 || status === 403) {
      try {
        res = await apiClient.post("/api/public/utils/parse-gmaps-url", { url });
      } catch (e2) {
        const detail = e2?.response?.data?.detail;
        throw new RouteParseError(detail || "Falha a contactar o servidor para resolver o link.");
      }
    } else {
      // Backend reachable failures (502 from short-link resolver, 500 etc).
      const detail = e?.response?.data?.detail;
      throw new RouteParseError(detail || "Falha a contactar o servidor para resolver o link.");
    }
  }
  const wps = res?.data?.waypoints;
  if (!Array.isArray(wps) || wps.length < 2) {
    throw new RouteParseError(
      "O servidor não conseguiu extrair 2 ou mais waypoints deste URL. Verifica que o link tem direcções (Maps → 3 pontos → Partilhar) e não apenas um marcador.",
    );
  }
  // Defensive: keep only valid `{lat,lng}` pairs.
  const validWps = wps.filter((p) =>
    typeof p?.lat === "number" && typeof p?.lng === "number" &&
    Math.abs(p.lat) <= 90 && Math.abs(p.lng) <= 180,
  );
  // Keep the full address on each point too — the routing endpoint
  // uses it as Google's ``address`` waypoint field for place-aware
  // snapping (matches Maps Web much closer than raw lat/lng). The
  // address is dropped from the on-map waypoints list (which only
  // cares about the short label) but preserved on `points` so the
  // routing call upstream can forward it.
  //
  // `via` flag (b99.9.30) — the backend marks shaping waypoints from
  // `!3m4!1m2` blocks with `via: true`. The Editor renders these as
  // small white dots on the polyline (no labelled marker) and the
  // routing call propagates the flag so Google Routes shapes the
  // polyline through them without treating them as named stops.
  const points = validWps.map((p) => ({
    lat: p.lat,
    lng: p.lng,
    address: typeof p.name === "string" ? p.name.trim() || null : null,
    via: p.via === true,
  }));
  if (points.length < 2) {
    throw new RouteParseError("Coordenadas inválidas no URL.");
  }
  // The backend now returns each waypoint with an optional `name`
  // pulled from the URL path segments (e.g. ``Col de Peyresourde,
  // 65240 Loudervielle``). Pick the first comma-separated chunk for
  // a compact tooltip label, falling back to a numeric ``Paragem N``
  // when the segment was a raw lat,lng (no name in the URL).
  //
  // b99.9.20 (2026-05-14) — Previously we stripped the first/last
  // entries here, on the assumption they'd be rendered as separate
  // Início/Fim markers and didn't belong in the side-panel list.
  // Field feedback: users expect the start and end hotels (or any
  // named extremity) to ALSO show up in the waypoints list so they
  // can hover/click them like any other stop. Keeping all of them
  // here doesn't hurt — `startName`/`endName` are still emitted
  // below for the dedicated map pins.
  //
  // b99.9.30 — `via` shaping points are emitted with `via: true` and
  // a null `name`; renderers should display them differently (no
  // "Paragem N" tooltip on the map, distinct row in the side panel).
  const labelOf = (raw, fallback) => {
    const t = typeof raw === "string" ? raw.trim() : "";
    return t ? t.split(",")[0].trim() : fallback;
  };
  // Numbering for "Paragem N" labels skips via-points so the user
  // sees Paragem 1, 2, 3 on the real stops even when shaping points
  // are interleaved.
  let stopCounter = 0;
  const waypoints = validWps.map((p) => {
    const isVia = p.via === true;
    if (!isVia) stopCounter += 1;
    const labelName = isVia ? null : labelOf(p.name, `Paragem ${stopCounter}`);
    // b99.10.4 (2026-05-23) — Carry the fuel classification across.
    // The backend sets `poi_kind` / `poi_brand` whenever the
    // classifier recognises the name (Repsol / BP / Galp / etc).
    // We ALSO run the same classifier client-side as a fallback so
    // that any cached response missing these fields still gets
    // categorised (handful of ms on a typical 10-stop route).
    let poiKind = p.poi_kind || null;
    let poiBrand = p.poi_brand || null;
    if (!isVia && !poiKind) {
      const probe = classifyFuelFromName(p.name);
      if (probe.isFuel) {
        poiKind = "fuel";
        poiBrand = poiBrand || probe.brand;
      }
    }
    return {
      lat: p.lat,
      lng: p.lng,
      via: isVia,
      name: labelName,
      fullName: typeof p.name === "string" ? p.name.trim() || null : null,
      poi_kind: poiKind,
      poi_brand: poiBrand,
    };
  });
  const namedWps = validWps.filter((p) => p.via !== true);
  const first = namedWps[0];
  const last = namedWps[namedWps.length - 1];
  return {
    name: null,
    points,
    waypoints,
    startName: labelOf(first?.name, null),
    endName: labelOf(last?.name, null),
    source: "google_maps_url",
  };
}

// b99.9.25 — `parseGoogleMapsURL` (client-side regex fallback) was
// removed in favour of the backend single-source-of-truth. The
// previous fallback didn't disambiguate between Google's
// `!1m5!1m1!1s{pid}` explicit stops and `!3m4!1m2` snap-to-road
// hints, so it would have silently mis-placed POI markers (the
// 2026-05-15 field bug) whenever the backend was unreachable.
// Better: surface the network error explicitly via
// `parseGoogleMapsURLViaBackend` and let the user retry online.

// ─── Go route (already in our shape) ──────────────────────────────

/**
 * Adopt a route from `GET /api/go-routes/:tripId` (or the list shape
 * from `GET /api/go-routes` after a follow-up fetch). The shape may
 * vary across endpoints; we handle the two common ones:
 *   - `coordinates: Array<{ lat, lng }>`
 *   - `route_geometry: { coordinates: [[lng, lat], …] }` (GeoJSON)
 */
export function parseGoRoute(goRoute) {
  if (!goRoute) throw new RouteParseError("Rota Go vazia.");
  const points = [];
  if (Array.isArray(goRoute.coordinates)) {
    for (const c of goRoute.coordinates) {
      if (typeof c?.lat === "number" && typeof c?.lng === "number") {
        points.push({ lat: c.lat, lng: c.lng, ele: c.alt, time: c.timestamp });
      }
    }
  } else if (goRoute.route_geometry?.coordinates) {
    for (const [lng, lat, ele] of goRoute.route_geometry.coordinates) {
      points.push({ lat, lng, ele });
    }
  }
  if (!points.length) {
    throw new RouteParseError("Esta rota Go não tem pontos GPS para mostrar.");
  }
  return {
    name: goRoute.name || goRoute.title || null,
    points,
    waypoints: [],
    source: "go_route",
  };
}
